/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../widgets/livewidget/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/ChangeVisTypeActionAPI',
	'../../../widgets/livewidget/visSwitcher/VisChangerFlyoutView',
	'../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI'
], function(_, stringResources, APIFactory, ChangeVisTypeActionAPI, VisChangerFlyoutView, ContentActionsProviderAPI) {

	return class ChangeVisTypeAction {

		constructor({ features }) {
			if(features) {
				this.dashboard = features.API;
				this.features = features;
				this.transaction = features.Transaction;
				/*
					Object to keep track of the section expand state. It is only updated when the user expands/collapses an section.
					{
						ALL: true/false/undefined,
						RECOMMENDED: true/false/undefined,
						:,
						:
					}
				}
				*/
				this.expandState = {};

				/*
				 If the visualization selected has its own icon defined, use it in the vis changer toolbar
				 This may be the case for integrators like PA as they define their own icons
				 in their provided recommender. The following provides a mapping between Vis Type and icon.
				 This is needed for the undo/redo case
				*/
				this.typeToIconMapping = {};
				features.ContentActions.registerProvider('changeVisTypeAction', this.getAPI());
			}
		}

		destroy() {
			this.dashboard = null;
			this.api = null;
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [ChangeVisTypeActionAPI, ContentActionsProviderAPI]);
			}
			return this.api;
		}

		_supportAction(idList, content) {
			const onlyOneElement = Boolean(idList.length === 1);

			const contentIsLiveWidget = Boolean(content && content.getType() === 'widget.live');

			const inSupportedMode = (this.dashboard.getMode() !== this.dashboard.MODES.EDIT_GROUP);

			const state = content && content.getFeature('state');
			const hasError = Boolean(state && !!state.getError());

			return onlyOneElement && contentIsLiveWidget && inSupportedMode && this._isInteractionEnabled('changeVis') && !hasError;
		}

		getContentActionList(idList) {
			const content = this.dashboard.getCanvas().getContent(idList[0]);
			if (this._supportAction(idList, content)) {
				let visualization = content.getFeature('Visualization');
				let changeVisIcon = this._getVisualizationIcon(visualization);
				let handler;
				let viewOptions = {
					height: 202,
					width: 402,
					content: content,
					dashboard: this.dashboard,
					expandState:this.expandState // Pass in the user selected expanded state of sections
				};
				let contribution = {
					name: 'changeVisTypeAction',
					label: stringResources.get('visChange'),
					icon: changeVisIcon.id,
					section: ContentActionsProviderAPI.ACTION_SECTION.SELECTION_DATA,
					order: 10,
					type: 'NextView',
					view: {
						module: 'dashboard-analytics/widgets/livewidget/visSwitcher/VisChangerFlyoutView',
						state: viewOptions
					},
					actions: {
						apply: this._changeVisType.bind(this, content)
					}
				};
				_.extend(contribution, {
					text: visualization.getDefinition().getLabel(),
					className: 'buttonWithText',
					callbacks: {
						registerCallback: (button) => {
							handler = ()=>{
								this._handleChangeType(button, visualization);
							};
							visualization.on('change:type', handler);
						},
						unregisterCallback: () => {
							visualization.off('change:type', handler);
						}
					}
				});
				return [contribution];
			}

			return [];
		}

		_handleChangeType(button, visualization) {

			button.updateState(
				{
					text: visualization.getDefinition().getLabel(),
					icon: this._getVisualizationIcon(visualization)
				});
		}

		_getSchematicIcon(visId, icons) {
			return visId === 'com.ibm.vis.schematicsPreview' ?  icons.getIcon('svgFile') : icons.getIcon('schematicVisual');
		}

		_getCustomVisIcon(visId, icons) {
			return visId === 'visualizationPreview' ?  icons.getIcon('codeFile32') : icons.getIcon('customVisual');
		}

		_getOutOfBoxVisIcon(definition, visId, icons) {
			const type = definition.getType() || '';
			return this.typeToIconMapping[type] || icons.getIcon(definition && definition.getIcon());
		}

		_getVisualizationIcon(visualization) {
			const isSchematic = visualization.getDefinition().getProperty('isSchematic');
			const isCustomVis = visualization.getDefinition().getProperty('isCustomVis');
			const visualizationId = visualization.getDefinition().getId();
			const icons = this.features['Icons'];
			let changeVisIcon;
			if (isSchematic) {
				changeVisIcon = this._getSchematicIcon(visualizationId, icons);
			} else if (isCustomVis) {
				changeVisIcon = this._getCustomVisIcon(visualizationId, icons);
			} else {
				changeVisIcon = this._getOutOfBoxVisIcon(visualization.getDefinition(), visualizationId, icons);
			}
			return!changeVisIcon ? icons.getIcon('visualizations-changeVisualization') : changeVisIcon;
		}

		_isInteractionEnabled(name) {
			const config = this.dashboard.getAppConfig('interactions') || {};
			if (config.hasOwnProperty(name) && [false, 'false'].indexOf(config[name]) !== -1) {
				return false;
			}
			return true;
		}

		_trackSetType (content, type, options = {}) {
			if (options.recommendedCategory && this.features.LiveWidgetSegment) {
				const trackInfo = {
					category: options.recommendedCategory,
					visualization: content.getFeature('Visualization'),
					content: content,
					info: {
						visId: type
					}
				};
				this.features.LiveWidgetSegment.track(trackInfo);
			}
		}

		_isUnsupportedVisType(content, type) {
			const visDef = this.features['VisDefinitions'].getByType(type);
			if (visDef && visDef.getProperty('supportsOLAP') === false) {
				const olapContentHelper = content.getFeature('Visualization.olapContentHelper');
				if(olapContentHelper && olapContentHelper.containsOlap()){
					this.features['Notification'].setMessage(stringResources.get('visualizationDoesNotSupportOLAP'));
					return true;
				}
			}
			return false;
		}

		/*
		 *Currently called by explore only that does not share the same VisChangerFlyoutView
		 */
		setVisType(contentId, type, transactionToken) {
			const content = this.dashboard.getCanvas().getContent(contentId);
			if (content) {
				if (!this._isUnsupportedVisType(content, type)) {
					const recommender = content.getFeature('Visualization.SmartsRecommender');
					if (recommender) {
						recommender.setType(type, transactionToken);
					}
				}
			}
		}

		_changeVisType(content, type, options = {}) {
			if (this._isUnsupportedVisType(content,type)){
				return;
			}
			this.expandState = options.expandState || {}; // Store expand state returned by view.
			/*
			 If the visualization selected has its own icon defined, use it in the vis changer toolbar
			 This may be the case for integrators like PA as they define their own icons
			 in their provided recommender
			*/
			this.typeToIconMapping[type] = options.icon;
			const recommender = content.getFeature('Visualization.SmartsRecommender');
			if (recommender){
				this._trackSetType(content, type, options);
				let transactionToken;
				if (!_.isEmpty(options.properties)) {
					transactionToken = this.transaction.startTransaction();
					_.each(options.properties, (value, name) => {
						content.setPropertyValue(name, value, transactionToken);
					});
				}
				return recommender.setType(type, transactionToken).then(() => {
					content.getFeature('Toolbar.internal').refresh();
				}).finally(() => {
					if (transactionToken) {
						this.transaction.endTransaction(transactionToken);
					}
				});
			}
		}

		openDialog(renderOptions, dialogOptions) {
			dialogOptions.actions = {
				apply: this._changeVisType.bind(this, dialogOptions.content)
			};
			if (!this.visChangeView) {
				this.visChangeView = new VisChangerFlyoutView(dialogOptions);
			}

			return this.visChangeView.render(renderOptions);
		}

		removeDialog () {
			if (this.visChangeView) {
				this.visChangeView.remove();
				this.visChangeView = null;
			}
		}
	};
});
